import { useMemo, useState } from 'react';
import { ViewModeFieldset } from '@/components/ViewModeFieldset';
import { Pencil } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Field, LiveScreen, QueryState, Select, formatDateTime, type Column } from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { DataGrid } from '@/components/DataGrid';
import { useRows } from '@/hooks/useResource';
import { callRpc, describeError } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { isAdmin } from '@/auth/session';
import { toast } from '@/lib/toast';

interface SetRow { id: string; set_key: string; name: string; description: string | null; is_compliance_impacting: boolean; active_value_count: number }
interface ValueRow {
  id: string; set_id: string; set_key: string; value_code: string; label: string; local_label: string | null;
  sort_order: number; attributes: Record<string, unknown> | null; status: string; updated_at: string;
}

/** The sets `api.fn_add_reference_value` accepts new entries for; the rest are seeded and code-bound. */
const ADDABLE = new Set(['kyc_type', 'land_tenure_type', 'species', 'caste', 'stakeholder_role', 'land_document_type', 'fsc_hhp']);

const GROUPS = [
  { id: 'identity', label: 'Identity' },
  { id: 'land_right', label: 'Land right' },
  { id: 'financial', label: 'Bank / financial' },
  { id: 'business', label: 'Business registration' },
];

/**
 * Reference data (`config`, back from the prototypes on 2026-09-27): the lists every dropdown reads -
 * species, units, KYC document types, land tenure and the rest. An admin corrects a label, gives it
 * its local-language name and puts the list in order; a KYC document type also gets its group, which
 * is what lets it satisfy an "any identity document" requirement. Codes are fixed: code and seeds
 * match on them.
 */
export default function ReferenceDataScreen() {
  const { profile } = useAuth();
  const admin = isAdmin(profile);
  const queryClient = useQueryClient();
  const sets = useRows<SetRow>('reference_sets', { orderBy: { column: 'name' } });
  const [setKey, setSetKey] = useState<string | null>(null);
  const current = setKey ?? sets.data?.[0]?.set_key ?? null;
  const values = useRows<ValueRow>('reference_values', { filters: { set_key: current }, orderBy: { column: 'sort_order' } }, Boolean(current));
  const [open, setOpen] = useState<ValueRow | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const isKyc = current === 'kyc_type';

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = [...(values.data ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
    return term ? rows.filter((row) => `${row.label} ${row.local_label ?? ''} ${row.value_code}`.toLowerCase().includes(term)) : rows;
  }, [values.data, search]);

  const columns: Array<Column<ValueRow>> = [
    { key: 'label', label: 'Value', render: (row) => <span className="cell-stack"><strong>{row.label}</strong><small className="muted">{row.value_code}</small></span> },
    { key: 'local_label', label: 'Local name', render: (row) => row.local_label ?? '--' },
    ...(isKyc ? [{ key: 'category', label: 'Group', render: (row: ValueRow) => {
      const category = String(row.attributes?.category ?? '');
      return category ? GROUPS.find((g) => g.id === category)?.label ?? category : <span className="muted">No group - satisfies no group requirement</span>;
    } }] : []),
    { key: 'sort_order', label: 'Order', align: 'right' },
    { key: 'updated_at', label: 'Changed', render: (row) => formatDateTime(row.updated_at) },
  ];

  const set = sets.data?.find((row) => row.set_key === current);
  return (
    <LiveScreen subtitle="The lists every dropdown reads. Correct a name, add a local name, put a list in order." title="Reference data">
      <QueryState empty="No reference lists yet." query={sets}>
        <div className="toolbar-row">
          <Field label="List">
            <Select onChange={(value) => { setSetKey(value); setSearch(''); }} value={current ?? ''}
                    options={(sets.data ?? []).map((row) => ({ id: row.set_key, label: `${row.name} (${row.active_value_count})` }))} />
          </Field>
          {admin && current && ADDABLE.has(current) && (
            <button className="primary" onClick={() => setOpen('new')} type="button">Add to {set?.name ?? 'list'}</button>
          )}
        </div>
        {set?.is_compliance_impacting && <p className="muted">This list feeds compliance checks. Change names with care; codes never change.</p>}
        <QueryState empty="Nothing in this list yet." query={values}>
          <DataGrid columns={columns} exportName={`reference-${current ?? ''}`} onRowClick={admin ? (row) => setOpen(row) : undefined}
                    onSearchChange={setSearch} rows={visible} screenKey="reference.data" search={search} selectedId={open && open !== 'new' ? open.id : undefined} />
        </QueryState>
      </QueryState>
      {open && current && (
        <EditValue key={open === 'new' ? 'new' : open.id} onClose={() => setOpen(null)} row={open} setKey={current} setName={set?.name ?? ''}
                   onSaved={() => { void queryClient.invalidateQueries({ queryKey: ['reference_values'] }); void queryClient.invalidateQueries({ queryKey: ['reference_sets'] }); }} />
      )}
    </LiveScreen>
  );

}

function EditValue({ row, setKey, setName, onClose, onSaved }: {
  row: ValueRow | 'new'; setKey: string; setName: string; onClose: () => void; onSaved: () => void;
}) {
  const isNew = row === 'new';
  const [label, setLabel] = useState(isNew ? '' : row.label);
  const [local, setLocal] = useState(isNew ? '' : row.local_label ?? '');
  const [order, setOrder] = useState(isNew ? '' : String(row.sort_order));
  const [group, setGroup] = useState(isNew ? '' : String(row.attributes?.category ?? ''));
  const [busy, setBusy] = useState(false);
  // An existing value opens to be read; Edit unlocks it (owner, 2026-09-27).
  const [viewing, setViewing] = useState(!isNew);
  const isKyc = setKey === 'kyc_type';

  async function save() {
    if (!label.trim()) { toast.error('Give the value a name.'); return; }
    if (isKyc && !group) { toast.error('Choose the group this document belongs to, or it will satisfy no group requirement.'); return; }
    setBusy(true);
    try {
      let id = isNew ? '' : row.id;
      if (isNew) id = await callRpc<string>('fn_add_reference_value', { p_set_key: setKey, p_label: label.trim() });
      await callRpc('fn_update_reference_value', {
        p_value_id: id, p_label: label.trim(), p_local_label: local.trim() || null,
        p_sort_order: order.trim() ? Number(order) : null, p_category: isKyc ? group : null,
      });
      toast.success(isNew ? `Added to ${setName}.` : 'Saved.');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(describeError(error as { message?: string; code?: string }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <RecordDrawer
      record={isNew ? null : { table: 'ref.reference_values', id: row.id, invalidate: ['reference_values'] }}
      footer={viewing ? <>
        <button onClick={onClose} type="button">Close</button>
        <button className="primary" onClick={() => setViewing(false)} type="button"><Pencil size={14} /> Edit</button>
      </> : <>
        <button onClick={onClose} type="button">Cancel</button>
        <button className="primary" disabled={busy} onClick={() => void save()} type="button">{isNew ? 'Add' : 'Save'}</button>
      </>}
      onClose={onClose} open
      subtitle={isNew ? `A new entry in ${setName}. Its code is made from the name and never changes.` : `${setName} · code ${row.value_code}`}
      title={isNew ? `Add to ${setName}` : row.label}
    >
      <ViewModeFieldset viewing={viewing}>
      <Field label="Name" required>
        <input onChange={(event) => setLabel(event.target.value)} placeholder="e.g. Babool (Acacia nilotica)" value={label} />
      </Field>
      <Field hint="The name in the local language, shown beside the English one." label="Local name">
        <input onChange={(event) => setLocal(event.target.value)} value={local} />
      </Field>
      <Field hint="Lower numbers come first in the dropdown." label="Order">
        <input inputMode="numeric" onChange={(event) => setOrder(event.target.value.replace(/[^0-9]/g, ''))} value={order} />
      </Field>
      {isKyc && (
        <Field hint="Any document in a group satisfies a requirement for that group." label="Group" required>
          <Select onChange={setGroup} options={GROUPS} placeholder="Choose a group" value={group} />
        </Field>
      )}
      </ViewModeFieldset>
    </RecordDrawer>
  );
}
